import React from "react";
import { useNavigate } from "react-router-dom";
import "./pro1.css";

import {
  GraduationCap,
  Zap,
  HardHat,
  Palette,
  Activity,
  Wrench,
  Truck,
  BrainCircuit,
} from "lucide-react";

import imgIums from "../assets/assets/card-img-iums.jpg";
import imgSolva from "../assets/assets/card-img-solvarayz.jpg";
import imgCons from "../assets/assets/card-img-constech.jpg";
import imgVinterio from "../assets/assets/card-img-vinterio.jpg";
import imgHplus from "../assets/assets/card-img-hplus.jpg";
import imgClock24 from "../assets/assets/card-img-clock24.jpg";
import imgTravelXp from "../assets/assets/card-img-travelxp.jpg";
import imgXpertMile from "../assets/assets/card-img-xpertmile.jpg";

const products = [
  {
    icon: GraduationCap,
    img: imgIums,
    title: "iUMS",
    productId: "iums-platform",
    category: "Edu Tech",
    desc: "A comprehensive education management platform digitizing academic, administrative, fee, and examination workflows. Empowering schools and universities with real-time analytics, automated student portals, and seamless NAAC accreditation compliance.",
    list: [
      "Academic & Admin",
      "Student & Faculty",
      "Financials & Fees",
      "Attendance & Exams",
      "Admissions Portal",
      "LMS Integration",
      "Parent Portal",
    ],
  },
  {
    icon: Zap,
    img: imgSolva,
    title: "SolvaRayz",
    productId: "solvarayz",
    category: "CRM & ERP · CLOUD",
    desc: "One system for the whole business. SolvaRayz runs leads, sales, WhatsApp marketing, accounting, inventory, projects, HR and support from one login with 12 unified modules and unlimited users.",
    list: [
      "CRM & Lead Management",
      "Sales & AI Quotations",
      "WhatsApp Marketing",
      "Accounting & GST Invoices",
      "Inventory & Stock",
      "Projects & Operations",
      "HR & Role-Based Access",
    ],
  },
  {
    icon: HardHat,
    img: imgCons,
    title: "ConsTech",
    productId: "constech",
    category: "Construction & Real Estate",
    desc: "Integrated construction ERP platform covering BOQ estimation, procurement, workforce, and project tracking. Eliminating cost overruns with daily progress logs, equipment tracking, and sub-contractor milestone management.",
    list: [
      "BOQ Estimation",
      "Procurement",
      "Workforce ERP",
      "Finance Tracking",
      "Subcontractor Portal",
      "Site Safety Logs",
      "Equipment Tracking",
    ],
  },
  {
    icon: Palette,
    img: imgVinterio,
    title: "vInterio",
    productId: "vinterio",
    category: "INTERIOR CRM & ERP",
    desc: "Specialized CRM & ERP for interior design firms, covering quotations, materials, vendors, and site execution. Enhancing client experience with digital mood boards, 3D design reviews, and automated milestone billing.",
    list: [
      "Quotations & Estimates",
      "Design Catalog",
      "Materials & Vendors",
      "Site Execution",
      "3D Model Reviews",
      "Client Approvals",
      "Budget Tracking",
    ],
  },
  {
    icon: Activity,
    img: imgHplus,
    title: "H+",
    productId: "hplus",
    category: "HEALTHCARE PLATFORM",
    desc: "Unified hospital management platform covering patient care, appointments, EMR/EHR, pharmacy, and billing. Ensuring 100% HIPAA-compliant clinical workflows, instant TPA claims, and reduced OPD patient wait times.",
    list: [
      "EMR & Patient Care",
      "Appointments",
      "Pharmacy & Lab",
      "Billing & Claims",
      "Inpatient Ward ERP",
      "Telehealth Portal",
      "Insurance Processing",
    ],
  },
  {
    icon: Wrench,
    img: imgClock24,
    title: "Clock24",
    productId: "clock24",
    category: "HOME SERVICES",
    desc: "On-demand mobile platform connecting customers with trusted home repair, plumbing, and electrical experts. Enabling real-time GPS technician dispatch, instant digital payments, and verified rating systems.",
    list: [
      "Booking & Service",
      "Professional Jobs",
      "Instant Payments",
      "Mobile GPS Tracking",
      "Customer Ratings",
      "Automated Invoices",
      "Expert Verification",
    ],
  },
  {
    icon: Truck,
    img: imgTravelXp,
    title: "TravelXp",
    productId: "travelxp",
    category: "Fleet Management System",
    desc: "Complete fleet management platform for vehicle tracking, trip management, driver analytics, and fuel alerts. Reducing fleet operating costs through live GPS route optimization, maintenance schedules, and fuel theft logs.",
    list: [
      "Live Vehicle GPS",
      "Driver Management",
      "Fuel Alerts & Logs",
      "Fleet Analytics",
      "Route Optimization",
      "Maintenance Schedules",
      "Trip Dispatch",
    ],
  },
  {
    icon: BrainCircuit,
    img: imgXpertMile,
    title: "XpertMile",
    productId: "xpertmile",
    category: "EXPERT COLLABORATION",
    desc: "Unified platform facilitating expert collaboration, knowledge exchange, and AI analytics for organizations. Driving enterprise innovation with secure video workshops, document sharing, and global advisory networks.",
    list: [
      "Expert Collaboration",
      "Knowledge Exchange",
      "AI Analytics",
      "Security & Access",
      "Video Workshops",
      "Advisory Network",
      "Document Sharing",
    ],
  },
];

const ProductsGrid = () => {
  const navigate = useNavigate();

  const handleProductClick = (e, productId) => {
    e.stopPropagation();
    navigate(`/products/${productId}`);
  };

  return (
    <div className="products-section" id="products-section">
      <div className="products-wrapper">
        {/* Header Section */}
        <div className="products-header section-header">
          <h2 className="products-title section-title">Our Products</h2>
          <p className="products-description section-description">
            Unlock powerful automation for effortless growth. Explore our
            innovative software solutions designed to streamline your operations
            and drive business success.
          </p>
        </div>

        {/* 4-Cards per Row Grid with Hover Information Matter Reveal */}
        <div className="products-grid-4col">
          {products.map((item, index) => {
            const ProductIcon = item.icon;
            return (
              <div
                key={index}
                className="product-card-interactive"
                onClick={(e) => handleProductClick(e, item.productId)}
              >
                {/* Image Area on Top */}
                <div className="product-card-image-wrap">
                  <img
                    src={item.img}
                    alt={item.title}
                    className="product-card-top-img"
                  />
                  <div className="product-card-image-overlay"></div>

                  {/* Always Visible Default Label & Title Footer */}
                  <div className="product-card-default-footer">
                    <span className="product-category-label-top">
                      {item.category}
                    </span>
                    <h3 className="product-card-title-top">{item.title}</h3>
                  </div>
                </div>

                {/* Hover Reveal Information Matter Layer */}
                <div className="product-card-matter-layer">
                  <div className="product-card-matter-header">
                    <div className="product-card-icon-box">
                      <ProductIcon size={20} />
                    </div>
                    <div>
                      <span className="product-category-label-hover">
                        {item.category}
                      </span>
                      <h3 className="product-card-title-hover">{item.title}</h3>
                    </div>
                  </div>

                  <p className="product-card-desc-hover">{item.desc}</p>

                  <div className="product-card-tags-hover">
                    {item.list.map((feature, idx) => (
                      <span key={idx} className="product-card-tag-pill-hover">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProductsGrid;
